interface ScrollResponsiveComposerHeaderOptions {
  directionThresholdPx: number
  transitionMs: number
  requestFrame?: (callback: FrameRequestCallback) => number
  cancelFrame?: (handle: number) => void
}

type ScrollDirection = 'down' | 'up'
type HeaderVisibility = 'hidden' | 'visible'

export function installScrollResponsiveComposerHeader(
  composer: HTMLElement,
  scrollHost: HTMLElement,
  options: ScrollResponsiveComposerHeaderOptions,
): () => void {
  const ownerWindow = scrollHost.ownerDocument.defaultView ?? window
  const requestFrame = options.requestFrame ?? ownerWindow.requestAnimationFrame.bind(ownerWindow)
  const cancelFrame = options.cancelFrame ?? ownerWindow.cancelAnimationFrame.bind(ownerWindow)
  const threshold = Math.max(1, options.directionThresholdPx)
  const previousTransitionDuration = composer.style.getPropertyValue('--composer-header-transition-duration')
  composer.style.setProperty(
    '--composer-header-transition-duration',
    `${Math.max(0, options.transitionMs)}ms`,
  )
  let lastScrollTop = Math.max(0, scrollHost.scrollTop)
  const header = composer.querySelector<HTMLElement>('.composer__topline')
  const headerOffset = Math.max(0, header?.getBoundingClientRect().height ?? 0)
  let travel = 0
  let direction: ScrollDirection | null = null
  let pendingFrame: number | null = null

  const setVisibility = (visibility: HeaderVisibility) => {
    if (composer.dataset.noteHeaderVisibility !== visibility) {
      composer.dataset.noteHeaderVisibility = visibility
      travel = 0
      direction = null
    }
  }

  const update = () => {
    pendingFrame = null
    const scrollTop = Math.max(0, scrollHost.scrollTop)

    if (scrollTop <= 0) {
      setVisibility('visible')
      direction = null
      travel = 0
      lastScrollTop = 0
      return
    }

    const delta = scrollTop - lastScrollTop
    if (delta === 0) return
    const nextDirection: ScrollDirection = delta > 0 ? 'down' : 'up'

    if (nextDirection !== direction) {
      direction = nextDirection
      travel = 0
    }
    travel += Math.abs(delta)

    if (travel >= threshold) {
      if (direction === 'down' && scrollTop >= headerOffset) setVisibility('hidden')
      else if (direction === 'up') setVisibility('visible')
    }

    lastScrollTop = scrollTop
  }

  const onScroll = () => {
    if (pendingFrame !== null) return
    pendingFrame = requestFrame(update)
  }

  composer.dataset.noteHeaderVisibility = 'visible'
  scrollHost.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    scrollHost.removeEventListener('scroll', onScroll)
    if (pendingFrame !== null) cancelFrame(pendingFrame)
    if (previousTransitionDuration) {
      composer.style.setProperty('--composer-header-transition-duration', previousTransitionDuration)
    } else {
      composer.style.removeProperty('--composer-header-transition-duration')
    }
  }
}
